import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useInterestTags() {
  return useQuery({
    queryKey: ["interest_tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("interest_tags")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });
}

export function useProfileInterests() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["profile_interests", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profile_interests")
        .select("tag_id, interest_tags(id, name, category)")
        .eq("user_id", user!.id);
      if (error) throw error;
      return data;
    },
  });

  const setInterests = useMutation({
    mutationFn: async (tagIds: string[]) => {
      if (!user) throw new Error("Not authenticated");
      const uid = user.id;

      // Delete existing then insert new — check both operations
      const { error: delError } = await supabase
        .from("profile_interests")
        .delete()
        .eq("user_id", uid);
      if (delError) throw delError;

      if (tagIds.length === 0) return;

      const rows = tagIds.map((tag_id) => ({ user_id: uid, tag_id }));
      const { error } = await supabase.from("profile_interests").insert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile_interests", user?.id] });
    },
  });

  return {
    interests: query.data ?? [],
    isLoading: query.isLoading,
    setInterests,
  };
}
